import { useState, useContext, memo, ChangeEvent } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete,
  Button,
  FormControl,
  MenuItem,
  TextField,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Select,
  InputLabel
} from '@mui/material'
import { Box } from '@mui/system'
import DesplegableRubros from '../ABMProducts/components/DesplegableRubros'
import useRubros from '../../Products/hooks/rubros.hook'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { autocomplete } from '../../../services/autocomplete'
import { ITenant } from '../../AbmLocalPage/types'
import { modelos } from '.'
import { INITIAL_ROWS_PER_PAGE } from '../../Products/components/TableDataPagination'

export interface FiltrosIMG {
  pagina: number
  pageSize: number
  codRubro: string
  nombreRubro: string
  codLocal: string
  nombreLocal: string
  nombreProducto: string
  soloActivos: boolean
  estado: string
  publicadoDesde: any
  publicadoHasta: any
  idProducto: number
  idFoto: number
}

const estados = [
  { name: 'Todos', value: 'todos' },
  { name: 'Pendiente', value: 'pending' },
  { name: 'Procesadas (con caption)', value: 'caption' },
  { name: 'Con Errores', value: 'error' }
]

interface FiltrosProps {
  loading: boolean
  filtros: FiltrosIMG
  setFiltros: (filtros: FiltrosIMG) => void
  modelo: string
  setModelo: (m: string) => void
  searching: boolean
  setSearching: (ser: boolean) => void
  tenant: ITenant
  handleSincronizar: () => void
}

/* eslint-disable react/prop-types */
const FiltrosImagenes: React.FC<FiltrosProps> = ({
  loading,
  filtros,
  setFiltros,
  modelo,
  setModelo,
  searching,
  setSearching,
  tenant,
  handleSincronizar
}) => {
  const [autoCompletado, setAutoCompletado] = useState<string[]>([])
  const { flatRubrosHijos } = useRubros()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleAutoCompletado = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (value.length < 3) return

    autocomplete(tenant, value, filtros?.soloActivos)
      .then((result) => {
        setAutoCompletado(result.data.data)
      })
      .catch((error) => {
        console.log('error: ', error)
      })
  }

  const handleFormSubmit = () => {
    setSearching(true)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: '#f5f5f5',
          gap: '0.6rem',
          margin: isMobile ? 'auto' : '1rem 1rem 1rem 0.5rem',
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: '14px'
          },
          ...(isMobile && {
            width: '90%',
            marginTop: '1rem'
          })
        }}
      >
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 768px)': {
              width: '100%'
            }
          }}
        >
          <DesplegableRubros
            rubros={flatRubrosHijos}
            onChange={(r: any) => {
              setFiltros({
                ...filtros,
                codRubro: r?.codRubro,
                nombreRubro: r?.nombre,
                pageSize: INITIAL_ROWS_PER_PAGE,
                pagina: 1
              })
            }}
            handleFormSubmit={handleFormSubmit}
          />
        </FormControl>
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 768px)': {
              width: '100%'
            }
          }}
        >
          <Autocomplete
            options={autoCompletado}
            disabled={searching || loading}
            filterOptions={(x) => x}
            getOptionLabel={(option: any) => option?.nombre || ''}
            isOptionEqualToValue={(option: any, value: any) => option?.data === value?.data}
            onChange={(e, value: any) =>
              setFiltros({
                ...filtros,
                codLocal: value?.data || null,
                nombreLocal: value?.nombre,
                pageSize: INITIAL_ROWS_PER_PAGE,
                pagina: 1
              })
            }
            renderInput={(p) => (
              <TextField
                {...p}
                onKeyPress={(e) => e.key === 'Enter' && handleFormSubmit()}
                label="Local"
                variant="outlined"
                onChange={handleAutoCompletado}
              />
            )}
          />
        </FormControl>
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 768px)': {
              width: '100%'
            }
          }}
        >
          <TextField
            disabled={searching || loading}
            label="Nombre de producto"
            fullWidth
            value={filtros?.nombreProducto || ''}
            onChange={(e) =>
              setFiltros({ ...filtros, nombreProducto: e.target.value, pageSize: INITIAL_ROWS_PER_PAGE, pagina: 1 })
            }
          />
        </FormControl>
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 768px)': {
              width: '100%'
            }
          }}
        >
          <Autocomplete
            disabled={searching || loading}
            options={estados}
            getOptionLabel={(estado: any) => estado.name || ''}
            onChange={(event, value) =>
              setFiltros({ ...filtros, estado: value?.value || null, pageSize: INITIAL_ROWS_PER_PAGE, pagina: 1 })
            }
            renderInput={(params) => <TextField {...params} variant="outlined" label="Estado" />}
            renderOption={(props, estado) => (
              <MenuItem
                {...props}
                key={estado.value}
                value={estado.value}
                sx={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}
              >
                {estado.name}
              </MenuItem>
            )}
            onKeyPress={(e) => e.key === 'Enter' && handleFormSubmit()}
          />
        </FormControl>
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 768px)': { width: '100%' }
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              disabled={searching || loading}
              label={isMobile ? 'Publicado Desde' : 'Public. Desde'}
              format="DD/MM/YYYY"
              value={filtros?.publicadoDesde}
              onChange={(date: any) => {
                setFiltros({ ...filtros, publicadoDesde: date || null, pageSize: INITIAL_ROWS_PER_PAGE, pagina: 1 })
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  onKeyPress: (e) => e.key === 'Enter' && handleFormSubmit(),
                  value: filtros?.publicadoDesde
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 768px)': { width: '100%' }
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              disabled={searching || loading}
              label={isMobile ? 'Publicado Hasta' : 'Public. Hasta'}
              format="DD/MM/YYYY"
              value={filtros?.publicadoHasta}
              onChange={(date: any) => {
                setFiltros({ ...filtros, publicadoHasta: date || null, pageSize: INITIAL_ROWS_PER_PAGE, pagina: 1 })
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  onKeyPress: (e) => e.key === 'Enter' && handleFormSubmit(),
                  value: filtros?.publicadoHasta
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
        <FormControl
          sx={{
            width: 'auto',
            minWidth: 'fit-content',
            '@media (max-width: 768px)': { width: '100%' }
          }}
        >
          <FormControlLabel
            sx={{ flex: 1 }}
            control={
              <Checkbox
                checked={filtros?.soloActivos}
                onChange={(e) => {
                  setFiltros({ ...filtros, soloActivos: e.target.checked, pageSize: INITIAL_ROWS_PER_PAGE, pagina: 1 })
                }}
              />
            }
            label="Sólo activos"
          />
        </FormControl>
        <FormControl
          sx={{
            minWidth: '56px',
            width: 'auto',
            '@media (max-width: 768px)': {
              width: '100%'
            }
          }}
        >
          <Button
            disabled={searching || loading}
            variant="contained"
            size="large"
            onClick={handleFormSubmit}
            fullWidth
            sx={{
              height: '56px',
              '@media (max-width: 768px)': {
                display: 'flex',
                gap: '10px',
                height: '100%'
              }
            }}
          >
            <Box
              sx={{
                display: 'none',
                '@media (max-width: 768px)': {
                  display: 'block'
                }
              }}
            >
              Filtrar
            </Box>
            <Box
              sx={{
                display: 'block',
                '@media (max-width: 768px)': {
                  display: 'flex'
                }
              }}
            >
              <SearchIcon />
            </Box>
          </Button>
        </FormControl>
        <FormControl
          sx={{
            width: '80%',
            height: '56px',
            ...(!isMobile && { ml: '0.6rem' }),
            '@media (max-width: 768px)': {
              width: '100%'
            }
          }}
        >
          <InputLabel id="modelo-label">Modelo</InputLabel>
          <Select
            labelId="modelo-label"
            label="Modelo"
            disabled={loading}
            value={modelo}
            onChange={(e) => {
              setModelo(e.target.value)
            }}
          >
            {modelos.map((m: { name: string; value: string }) => (
              <MenuItem key={m.value} value={m.value}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          sx={{
            marginLeft: isMobile ? 'auto' : '0.3rem',
            width: 'auto',
            minWidth: 'fit-content',
            '@media (max-width: 768px)': { width: '100%' }
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={handleSincronizar}
            sx={{
              height: '56px',
              '@media (max-width: 768px)': {
                display: 'flex',
                gap: '10px',
                height: '100%'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isMobile ? (
              'Sincronizar Todo'
            ) : (
              <>
                Sinc.
                <br />
                Todo
              </>
            )}
          </Button>
        </FormControl>
      </Box>
    </>
  )
}

export default memo(FiltrosImagenes)
